import { useLayoutEffect } from "react";
import { Outlet, useLocation, useNavigationType } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";

const SCROLL_PREFIX = "jmh:scroll:";

export const Layout = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const locationKey = location.key || `${location.pathname}${location.search}`;

  useLayoutEffect(() => {
    // JMH owns restoration so Back/Forward returns to the exact prior viewport,
    // while a genuinely new navigation starts at the top of its destination.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    if (navigationType === "POP") {
      const saved = sessionStorage.getItem(`${SCROLL_PREFIX}${locationKey}`);
      const y = saved === null ? 0 : Number(saved);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: Number.isFinite(y) ? y : 0, behavior: "auto" });
      });
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }

    return () => {
      sessionStorage.setItem(`${SCROLL_PREFIX}${locationKey}`, String(window.scrollY));
    };
  }, [locationKey, navigationType]);

  useLayoutEffect(() => {
    if (!location.pathname.startsWith("/foundation")) return undefined;
    const originals = [];
    const set = (selector, tag, attrs) => {
      let node = document.head.querySelector(selector);
      const created = !node;
      if (created) {
        node = document.createElement(tag);
        document.head.appendChild(node);
      }
      const previous = Object.fromEntries(Object.keys(attrs).map(key => [key, node.getAttribute(key)]));
      Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
      originals.push(() => {
        if (created) node.remove();
        else Object.entries(previous).forEach(([key, value]) => value === null ? node.removeAttribute(key) : node.setAttribute(key, value));
      });
    };
    set('link[rel="icon"]', "link", { rel: "icon", type: "image/png", sizes: "32x32", href: "/brand/foundation-20260929-32.png" });
    set('link[rel="apple-touch-icon"]', "link", { rel: "apple-touch-icon", sizes: "180x180", href: "/brand/foundation-20260929-180.png" });
    set('link[rel="manifest"]', "link", { rel: "manifest", href: "/brand/foundation-20260929.webmanifest" });
    set('meta[name="apple-mobile-web-app-title"]', "meta", { name: "apple-mobile-web-app-title", content: "Jennae Michelle Foundation" });
    set('meta[name="apple-mobile-web-app-capable"]', "meta", { name: "apple-mobile-web-app-capable", content: "yes" });
    return () => originals.forEach(restore => restore());
  }, [location.pathname]);

  return (
    <div className="App jmh-public-layout min-h-screen flex flex-col bg-black-lux">
      <a className="jmh-skip-link" href="#main-content">Skip to content</a>
      <Nav />
      <main id="main-content" tabIndex={-1} className="flex-1 jmh-public-main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};
